'use client'

import { useState, useCallback } from 'react'

/**
 * Minimal hook for modal open/close state.
 * Each component manages its own modal - no provider or context needed.
 *
 * Usage:
 * ```tsx
 * const videoModal = useModal()
 *
 * return (
 *   <>
 *     <button onClick={videoModal.open}>Watch Video</button>
 *     <ModalManager type="video" {...videoModal} videoUrl="..." />
 *   </>
 * )
 * ```
 */
export function useModal(initialOpen = false) {
    const [isOpen, setIsOpen] = useState(initialOpen)

    const open = useCallback(() => setIsOpen(true), [])
    const close = useCallback(() => setIsOpen(false), [])
    const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

    return { isOpen, open, close, toggle }
}

export type UseModalReturn = ReturnType<typeof useModal>
